<template>
	<div :class="classes" ref="dropdown" :style="styles">
		<div ref="selector">
			<TeamingTextbox
				class="input"
				:disabled="disabled"
				:is-valid="isValid"
				:name="name"
				:placeholder="placeholder"
				:value="currentValue"
				@input="inputHandler"
			/>
		</div>
		<ul class="teaming-type-ahead__options" ref="options">
			<li v-for="option in matchedOptions"
				:key="option.value"
				class="teaming-type-ahead__option"
			>
				<slot name="options"
					:option="option"
					:selectedOption="selectedOption"
					:onOptionClicked="onOptionClicked"
				>
					<DropDownRow
						:option="option"
						:is-selected="option.value === selectedOption.value"
						@click="onOptionClicked( option, $event )"
					/>
				</slot>
			</li>
		</ul>
	</div>
</template>

<script>
import debounce from "lodash/debounce";

import TeamingTextbox from "./TeamingTextbox.vue";
import DropDownRow from "./DropDownRow.vue";

export const makeOption = ( {
	text = "",
	value = "",
} = {} ) => ( {
	text,
	value,
} );

export default {
	name: "TeamingTypeAhead",
	emits: [
		"change",
		"opening",
		"opened",
		"closing",
		"closed",
	],
	components: {
		TeamingTextbox,
		DropDownRow,
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		isValid: {
			type: Boolean,
			default: true,
		},
		name: {
			type: String,
			default: "",
		},
		/** see: makeOption */
		options: {
			type: Array,
			required: true,
		},
		placeholder: {
			type: String,
			default: "",
		},
		value: {
			type: String,
			default: "",
		},
		width: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			documentClickHandler: null,
			inputHandler: null,
			inputText: "",
			isDirty: false,
			isOpen: false,
			matchedOptions: [],
			matchedOptionsCache: {},
		};
	},
	computed: {
		classes() {
			return {
				"teaming-type-ahead": true,
				"text-default": true,
			};
		},
		styles() {
			const style = {};
			if ( this.width ) {
				style.width = this.width;
			}
			return style;
		},
		selectedOption() {
			const { options, value, } = this;

			return options.find( option => option.value === value ) ||
				makeOption();
		},
		currentValue() {
			return this.isDirty ?
				this.inputText :
				this.selectedOption.text;
		},
	},
	methods: {
		resize() {
			const { selector, options, } = this.$refs;
			if ( !( selector && options ) ) {
				return;
			}
			if ( this.width ) {
				options.style.width = this.width;
				return;
			}
			const rect = selector.getBoundingClientRect();
			const { left, right, } = rect;
			options.style.width = `${ right - left }px`;
		},
		async open() {
			this.$emit( "opening" );
			this.resize();
			this.isOpen = true;
			const { options, } = this.$refs;
			options.classList.add( "teaming-type-ahead__options--visible" );
			await this.$nextTick();
			this.$emit( "opened" );
		},
		async close() {
			this.$emit( "closing" );
			this.resize();
			this.isOpen = false;
			const { options, } = this.$refs;
			// By default, the options are not scrollable, but if an extending component
			// changes that behavior, we want to ensure that they are scrolled back to
			// the top before the component is closed. Note that this needs to happen
			// before the visibility has been removed.
			options.scrollTo( { top: 0, left: 0, } );
			options.classList.remove( "teaming-type-ahead__options--visible" );
			await this.$nextTick();
			this.$emit( "closed" );
		},
		async onOptionClicked( option, e ) {
			e.stopPropagation();

			await this.close();
			await this.$nextTick();
			this.$emit( "change", { ...option, } );
		},
		createHandlers() {
			this.clearHandlers();
			// @deprecated: prefer foundation.global.onClickOutside() instead
			this.documentClickHandler = e => {
				const dropdownElement = this.$refs.dropdown;
				const path = e.path || ( e.composedPath && e.composedPath() );
				if ( !path.includes( dropdownElement ) ) {
					this.close();
				}
			};
			document.addEventListener( "click", this.documentClickHandler );

			this.inputHandler = debounce( e => {
				this.inputText = e.target.value;
				const searchFor = this.inputText.toLowerCase().trim();
				this.isDirty = true;
				this.matchedOptions = [];

				if ( !searchFor ) {
					return this.close();
				}

				if ( this.matchedOptionsCache[ searchFor ] ) {
					this.matchedOptions = this.matchedOptionsCache[ searchFor ];
				} else {
					this.matchedOptionsCache[ searchFor ] = this.options.filter( option => {
						return option.text.toLowerCase().includes( searchFor );
					} );
					this.matchedOptions = this.matchedOptionsCache[ searchFor ];
				}

				return this.matchedOptions.length ?
					this.open() :
					this.close();
				// eslint-disable-next-line no-magic-numbers
			}, 500 );
		},
		clearHandlers() {
			this.inputHandler = null;
			if ( this.documentClickHandler ) {
				document.removeEventListener( "click", this.documentClickHandler );
				this.documentClickHandler = null;
			}
		},
	},
	watch: {
		value( /* newValue, oldValue*/ ) {
			// value updates should reset the dirty flag
			// so the text input gets its value from props,
			// not the temporary text the user has entered
			// before selecting an option (see: currentValue)
			if ( this.isDirty ) {
				this.isDirty = false;
			}
		},
	},
	created() {
		this.createHandlers();
	},
	beforeUnmount() {
		this.clearHandlers();
	},
	updated() {
		if ( this.disabled ) {
			this.$nextTick( () => this.close() );
		}
	},
};
</script>

<style lang="scss" scoped>
.teaming-type-ahead {
	display: inline-block;
	position: relative;
}

.teaming-type-ahead .input {
	width: 100%;
}

.teaming-type-ahead__selector {
	padding: 0.625rem 0.75rem; // 10px 12px
	background-color: $dropdown-background-color;
	border: 0.0625rem solid $dropdown-border-color; // 1px
	border-radius: 0.5rem; // 8px
	color: $dropdown-font-color;

	&:hover {
		border: 0.0625rem solid $dropdown-border-color--hover;
	}
}

.teaming-type-ahead__options {
	z-index: 10;
	list-style: none;
	margin: 0;
	padding: 0;
	background-color: $dropdown-background-color;
	border: 0.0625rem solid $dropdown-border-color; // 1px
	border-radius: 0.5rem; // 8px
	display: none;
	text-align: left;
	box-shadow: 0 0.1875rem 0.375rem 0.0625rem rgba(33, 45, 99, 0.1), 0 1.313rem 1.875rem -1.25rem rgba(33, 45, 99, 0.2);
	color: $dropdown-font-color;
	cursor: pointer;
}

// NOTE: padding/margins have to be applied at the option level to
// ensure that hover highlighting looks correct
.teaming-type-ahead__option {
	padding: 0.375rem 1rem; // 6px 16px

	&:first-of-type {
		margin-top: 0.375rem; // 6px
	}

	&:last-of-type {
		margin-bottom: 0.375rem; // 6px
	}

	&:hover {
		background-color: $dropdown-background-color--hover;
	}
}

.teaming-type-ahead__options--visible {
	display: block;
	position: absolute;
	width: 100%;
}
</style>
